import { useCallback, useEffect, useRef, useState } from 'react'

import { useWindowVirtualizer } from '@tanstack/react-virtual'

import {
  EyeIcon,
  IconPlayOutline
} from 'assets/svg/sprite'
import { usePreviewContext } from 'context/previewContext'
import { PostViewWrapper } from 'features/shared'
import { useGetMonthName } from 'helpers/hooks'
import { prefetchUpcomingPosts } from 'helpers/prefetchPosts'
import { useUserProfile } from 'store'
import type { IPost } from 'types/interfaces/ITypes'

type PreviewDataLike = {
  minimized: boolean
  updateSelectedPost: (
    postIdx: number,
    slideIdx: number,
    type: string,
    postData: IPost,
    dataQuery?: readonly any[]
  ) => void
  handleMinimize: (isMinimized: boolean) => void
  addModal: (
    postNumber: number,
    selectedPostSlide: number,
    type: string,
    postData: IPost | null,
    dataQuery: readonly any[]
  ) => void
  setInitialData: (
    userId: string,
    filter: string,
    location: any,
    filterQ?: any,
    query?: any[],
    newState?: any
  ) => void
}

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n))
}

function getWindowScrollTop() {
  return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0
}

function getWindowHeight() {
  return window.innerHeight || document.documentElement.clientHeight || 0
}

function safeStr(u: any): string {
  if (typeof u !== 'string') return ''
  return u.trim()
}

function isVideoReel(post: any): boolean {
  const videos = Array.isArray(post?.videos) ? post.videos : []
  const videosPreview = Array.isArray(post?.videos_preview)
    ? post.videos_preview
    : []

  return videos.length > 0 || videosPreview.length > 0
}

function getReelThumb(post: any): string {
  const videos = Array.isArray(post?.videos) ? post.videos : []
  const videosPreview = Array.isArray(post?.videos_preview) ? post.videos_preview : []
  const photos = Array.isArray(post?.photos) ? post.photos : []
  const photosPreview = Array.isArray(post?.photos_preview) ? post.photos_preview : []
  const media = videos[0] ?? videosPreview[0] ?? photos[0] ?? photosPreview[0]

  return (
    safeStr(media?.active_thumb) ||
    safeStr(media?.thumb_url) ||
    safeStr(media?.thumbnail) ||
    safeStr(media?.url)
  )
}

function ReelGridThumb({
  post,
  index,
  dataQuery,
  previewData,
}: {
  post: IPost
  index: number
  dataQuery: any
  previewData?: PreviewDataLike
}) {
  const thumb = getReelThumb(post)
  const isVideo = isVideoReel(post)
  const monthName = useGetMonthName()
  const fallbackPreviewData = usePreviewContext()
  const activePreviewData = previewData ?? fallbackPreviewData
  const userData = useUserProfile()

  const createdAt =
    (post as any)?.created_at ??
    (post as any)?.createdAt ??
    (post as any)?.date ??
    null

  const createdDate = createdAt ? new Date(createdAt) : null

  const isOwner =
    Number((post as any)?.user_id) === Number(userData?.id)

  const views = (post as any)?.views

  const dateLabel =
    createdDate && !Number.isNaN(createdDate.getTime())
      ? `${monthName.getMonthName(createdDate.getMonth(), 'shorthand')} ${createdDate.getDate()}`
      : '--'

  const handleClick = useCallback(() => {
    const {
      minimized,
      updateSelectedPost,
      handleMinimize,
      addModal,
      setInitialData,
    } = activePreviewData

    const uid = (post as any)?.user_id ?? userData?.id

    if (!uid) return

    setInitialData(uid, '', 'profile', undefined, dataQuery)

    if (minimized) {
      updateSelectedPost(index, 0, 'video', post, dataQuery)
      handleMinimize(false)
      return
    }

    addModal(index, 0, 'video', post, dataQuery)
  }, [activePreviewData, post, userData?.id, dataQuery, index])

  return (
    <button
      type="button"
      className="profilefeed__reelThumb"
      onClick={handleClick}
    >
      {thumb ? <img src={thumb} alt="" loading="lazy" /> : null}

      {isVideo ? (
        <div className="profilefeed__reelThumbPlay">
          <IconPlayOutline color="#ffffff" />
        </div>
      ) : null}

      <div className="profilefeed__reelThumbBubble profilefeed__reelThumbBubble--date">
        {dateLabel}
      </div>

      {isOwner ? (
        <div className="profilefeed__reelThumbBubble profilefeed__reelThumbBubble--views">
          <EyeIcon width="16" height="12" color="#ffffff" />
          <span>{views}</span>
        </div>
      ) : null}
    </button>
  )
}

type VirtualizedPostsFeedListProps = {
  feedPosts: IPost[]
  userId: string
  userData: any
  addDebounceImpression: (...args: any[]) => void
  dataQuery: any
  previewData?: PreviewDataLike

  fetchNextPage: () => void
  hasNextPage?: boolean
  isFetchingNextPage?: boolean

  estimateRowPx?: number
  rowGapPx?: number
  overscan?: number
  directionalOverscan?: number
  prefetchAhead?: number
  fetchThreshold?: number
  layout?: 'list' | 'grid'
}

export default function VirtualizedPostsFeedList({
  feedPosts,
  userId,
  userData,
  addDebounceImpression,
  dataQuery,
  previewData,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  estimateRowPx = 292,
  rowGapPx = 12,
  overscan = 1,
  directionalOverscan = 1,
  prefetchAhead = 1,
  fetchThreshold = 4,
  layout = 'list',
}: VirtualizedPostsFeedListProps) {
  const isGrid = layout === 'grid'
  const columns = isGrid ? 3 : 1
  const rowCount = isGrid ? Math.ceil(feedPosts.length / columns) : feedPosts.length

  const prefetchedUrlsRef = useRef<Set<string>>(new Set())

  const [dir, setDir] = useState<'up' | 'down'>('down')
  const lastTopRef = useRef(0)

  useEffect(() => {
    let raf = 0

    const onScroll = () => {
      if (raf) return

      raf = requestAnimationFrame(() => {
        raf = 0

        const st = getWindowScrollTop()
        setDir(st >= lastTopRef.current ? 'down' : 'up')
        lastTopRef.current = st
      })
    }

    lastTopRef.current = getWindowScrollTop()
    window.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])

  const rowVirtualizer = useWindowVirtualizer({
    count: rowCount,
    estimateSize: index => {
      const post = feedPosts[index]

      const hasPortraitMedia =
        post?.photos?.some(media => media.orientation === 'Portrait') ||
        post?.videos?.some(media => media.orientation === 'Portrait') ||
        post?.photos_preview?.some(media => media.orientation === 'Portrait') ||
        post?.videos_preview?.some(media => media.orientation === 'Portrait')

      if (post?.poll || hasPortraitMedia) return 462
      if (post?.goal) return 450
      return estimateRowPx
    },
    overscan: 0,
    rangeExtractor: range => {
      const count = rowCount
      if (!count) return []

      const base = Math.max(0, overscan)
      const extra = Math.max(0, directionalOverscan)
      const topExtra = base + (dir === 'up' ? extra : 0)
      const bottomExtra = base + (dir === 'down' ? extra : 0)

      const start = Math.max(0, range.startIndex - topExtra)
      const end = Math.min(count - 1, range.endIndex + bottomExtra)

      const out: number[] = []

      for (let i = start; i <= end; i++) {
        out.push(i)
      }

      return out
    },
    getItemKey: i => {
      if (!isGrid) return String((feedPosts[i] as any)?.id ?? i)

      const start = i * columns

      return (
        feedPosts
          .slice(start, start + columns)
          .map((p: any) => p?.id)
          .filter(Boolean)
          .join('-') || String(i)
      )
    },
  })

  useEffect(() => {
    if (!feedPosts.length) return

    rowVirtualizer.measure()
  }, [feedPosts.length, rowVirtualizer, layout])

  const measureRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      if (!el) return

      el.setAttribute('data-index', String(index))
      rowVirtualizer.measureElement(el)
    },
    [rowVirtualizer]
  )

  const [activeRowIndex, setActiveRowIndex] = useState(0)
  const activeRowIndexRef = useRef(0)

  useEffect(() => {
    let raf = 0

    const compute = () => {
      raf = 0

      const items = rowVirtualizer.getVirtualItems()
      if (!items.length) return

      const centerY = getWindowScrollTop() + getWindowHeight() * 0.5
      let best = items[0].index

      for (let i = 0; i < items.length; i++) {
        const it = items[i]
        const start = it.start
        const end = it.start + it.size

        if (centerY >= start && centerY <= end) {
          best = it.index
          break
        }

        if (centerY > end) best = it.index
      }

      if (best !== activeRowIndexRef.current) {
        activeRowIndexRef.current = best
        setActiveRowIndex(best)
      }
    }

    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(compute)
    }

    compute()
    window.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [rowVirtualizer])

  const activePostIndex = isGrid ? activeRowIndex * columns : activeRowIndex

  const prefetchSigRef = useRef('')
  const prefetchAbortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (!feedPosts.length || prefetchAhead <= 0) return

    const aheadCount = isGrid ? columns * prefetchAhead : prefetchAhead

    const a =
      dir === 'down'
        ? [
            clamp(activePostIndex + 1, 0, feedPosts.length),
            clamp(activePostIndex + 1 + aheadCount, 0, feedPosts.length),
          ]
        : [
            clamp(activePostIndex - aheadCount, 0, feedPosts.length),
            clamp(activePostIndex, 0, feedPosts.length),
          ]

    const [start, end] = a
    const slice = feedPosts.slice(start, end)
    const ids = slice.map((p: any) => p?.id).filter(Boolean)

    if (!ids.length) return

    const sig = `${layout}:${dir}:${activePostIndex}:${start}:${end}:${ids.join(',')}`

    if (prefetchSigRef.current === sig) return

    prefetchSigRef.current = sig

    prefetchAbortRef.current?.abort()

    const ac = new AbortController()
    prefetchAbortRef.current = ac

  prefetchUpcomingPosts(slice, ac.signal, {
    mode: isGrid ? 'grid' : 'list',
    maxMediaPerPost: isGrid ? 1 : 2,
    concurrency: isGrid ? 3 : 4,
    alreadyPrefetched: prefetchedUrlsRef.current
  }).catch(() => {})

    return () => ac.abort()
  }, [feedPosts, dir, activePostIndex, prefetchAhead, isGrid, columns, layout])

  const lastFetchSigRef = useRef('')

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || !feedPosts.length) return

    const remaining = feedPosts.length - 1 - activePostIndex
    const sig = `${layout}:${feedPosts.length}:${activePostIndex}:${remaining}:${hasNextPage}:${isFetchingNextPage}`

    if (sig === lastFetchSigRef.current) return

    lastFetchSigRef.current = sig

    if (remaining <= fetchThreshold) fetchNextPage()
  }, [
    layout,
    feedPosts.length,
    activePostIndex,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    fetchThreshold,
  ])

  const virtualItems = rowVirtualizer.getVirtualItems()

  return (
    <div style={{ position: 'relative', width: '100%', height: rowVirtualizer.getTotalSize() }}>
      {virtualItems.map(vRow => {
        if (isGrid) {
          const startIndex = vRow.index * columns
          const rowPosts = feedPosts.slice(startIndex, startIndex + columns)

          return (
            <div
              key={vRow.key}
              data-index={vRow.index}
              ref={measureRef(vRow.index)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${vRow.start}px)`,
                paddingBottom: rowGapPx,
              }}
            >
              <div className="profilefeed__reelsGrid">
                {rowPosts.map((post, i) => (
                  <ReelGridThumb
                    key={post.id}
                    post={post}
                    index={startIndex + i}
                    dataQuery={dataQuery}
                    previewData={previewData}
                  />
                ))}
              </div>
            </div>
          )
        }

        const post = feedPosts[vRow.index]

        if (!post) return null

        return (
          <div
            key={vRow.key}
            data-index={vRow.index}
            ref={measureRef(vRow.index)}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${vRow.start}px)`,
              paddingBottom: rowGapPx,
            }}
          >
            <PostViewWrapper
              post={post}
              index={vRow.index}
              lastPostRef={undefined as any}
              addDebounceImpression={addDebounceImpression}
              userData={userData}
              userId={userId}
              isLastPost={vRow.index === feedPosts.length - 1}
              dataQuery={dataQuery}
            />
          </div>
        )
      })}
    </div>
  )
}